import { Lang } from '../../i18n';
import { BIKES, HELMETS, KITS } from './livery';
import { DEFAULT_BIKE_FOR, FLEET } from '../../sim/bikes';
import { detectLang, isLang } from './lang';
import { CAMERA_VIEWS, type CameraView } from '../../view/logic/chase';

/** Graphics: Auto (fitted to the device once, src/view/logic/worldFit.ts) or a fixed world (src/view/logic/registry.ts CAPS). */
export type QualitySetting = 'auto' | 'full' | 'medium' | 'light';
export const QUALITY_OPTIONS: readonly QualitySetting[] = ['auto', 'full', 'medium', 'light'];

export interface Settings {
  lang: Lang; quality: QualitySetting; master: number; engine: number; sfx: number; ambience: number;
  bike: string; kit: string; helmet: string; hints: boolean;
  /**
   * Control mode (the key keeps its old name so saved choices carry over): true = Simplified, the assists of
   * src/sim/assist.ts (one brake, automatic body, stance and foot, balance, ABS, traction control, air levelling,
   * forgiving crashes); false = Real, everything by hand.
   */
  beginner: boolean;
  /** Last picked fleet bike (src/sim/bikes) per discipline: `model` on the track levels, `enduroModel` on the enduro route. `bike` is the plastics livery. */
  model: string;
  enduroModel: string;
  /** Riding camera: chase (behind the bike) or fpv (helmet cam). C / View / the HUD camera button cycle it. */
  camera: CameraView;
  /** "Great jump! Save a clip?" toasts after a highlight (src/game/highlights.ts); off in Settings. */
  clipTips: boolean;
}


export const defaultSettings = (langs: readonly string[] = []): Settings => ({
  lang: detectLang(langs), quality: 'auto', master: 0.8, engine: 0.9, sfx: 1, ambience: 0.5, bike: 'factory', kit: 'school', helmet: 'airoh', hints: true, beginner: true, /* new players (and phones) start in Simplified mode */ model: DEFAULT_BIKE_FOR.track, enduroModel: DEFAULT_BIKE_FOR.enduro, camera: 'chase', clipTips: true,
});

const vol = (v: unknown, d: number) => (typeof v === 'number' && Number.isFinite(v) ? Math.max(0, Math.min(1, v)) : d);

export function sanitizeSettings(raw: unknown, langs: readonly string[] = []): Settings {
  const d = defaultSettings(langs);
  const r = (raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {}) as Record<string, unknown>;
  return {
    lang: isLang(r.lang) ? r.lang : d.lang,
    quality: (QUALITY_OPTIONS as readonly unknown[]).includes(r.quality) ? (r.quality as QualitySetting) : d.quality,
    master: vol(r.master, d.master), engine: vol(r.engine, d.engine), sfx: vol(r.sfx, d.sfx), ambience: vol(r.ambience, d.ambience),
    bike: BIKES.some((b) => b.id === r.bike) ? (r.bike as string) : d.bike,
    kit: KITS.some((b) => b.id === r.kit) ? (r.kit as string) : d.kit,
    helmet: HELMETS.some((b) => b.id === r.helmet) ? (r.helmet as string) : d.helmet,
    hints: typeof r.hints === 'boolean' ? r.hints : d.hints,
    beginner: typeof r.beginner === 'boolean' ? r.beginner : d.beginner,
    model: FLEET.some((b) => b.id === r.model && b.discipline === 'track') ? (r.model as string) : d.model,
    enduroModel: FLEET.some((b) => b.id === r.enduroModel && b.discipline === 'enduro') ? (r.enduroModel as string) : d.enduroModel,
    camera: (CAMERA_VIEWS as readonly unknown[]).includes(r.camera) ? (r.camera as CameraView) : d.camera,
    clipTips: typeof r.clipTips === 'boolean' ? r.clipTips : d.clipTips,
  };
}

export type KeyValue = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
export const SETTINGS_KEY = 'megamx.settings.v1';
export function loadSettings(store: KeyValue | null, langs: readonly string[]): Settings {
  try {
    const raw = store?.getItem(SETTINGS_KEY);
    return sanitizeSettings(raw ? JSON.parse(raw) : null, langs);
  } catch { return defaultSettings(langs); }
}

export function saveSettings(store: KeyValue | null, s: Settings): void {
  try {
    store?.setItem(SETTINGS_KEY, JSON.stringify(s));
  } catch { /* storage blocked: settings live for the session */ }
}
